<template>
    <service-card
        :service-tips="serviceTips"
        :service="service"
        icon="mdi-lightning-bolt-outline"
        :is-first="isFirst"
        :extra-service-name="info.serviceName"
        :order="order"
        :is-info-loading="isInfoLoading"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service-check-in="$filters.dateFormat(service.startDateTime)"
                :service-check-out="
                    service.startDateTime !== service.endDateTime ? $filters.dateFormat(service.endDateTime) : null
                "
                :service="service"
                :service-image="mainImage"
                :is-package="isPackage"
                :tariff-name="service.serviceDetails[0].tariffName"
                :order-end-date="orderEndDate"
                :service-label="
                    serviceDetails && `${serviceDetails.cabinCategoryName} / ${serviceDetails.cabinTypeName}`
                "
                :order="order"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <v-skeleton-loader v-if="isInfoLoading" type="paragraph" />
                <div v-if="departureLocation" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-city </v-icon>
                        <span class="text-body-2">{{ $t('transfers.from') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${departureLocation.name}, ${departureLocation.countryName}` }}
                    </span>
                </div>
                <div v-if="arrivalLocation" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-city </v-icon>
                        <span class="text-body-2">{{ $t('transfers.to') }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${arrivalLocation.name}, ${arrivalLocation.countryName}` }}
                    </span>
                </div>
                <div v-if="serviceDetails.shipName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-ship-wheel </v-icon>
                        <span class="text-body-2">{{ $t('cruises.ship_name') }}</span>
                    </span>
                    <span class="order-info-base-info__text">{{ `${serviceDetails.shipName}` }}</span>
                </div>
                <div v-if="serviceDetails.cruiseLine" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-ferry </v-icon>
                        <span class="text-body-2"> {{ $t('cruises.cruise_line') }}</span>
                    </span>
                    <span class="order-info-base-info__text">{{ `${serviceDetails.cruiseLine}` }}</span>
                </div>
                <div v-if="serviceDetails.cabinNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-ship-wheel </v-icon>
                        <span class="text-body-2"> {{ $t('cruises.cabin_number') }}</span>
                    </span>
                    <span class="order-info-base-info__text">{{ `${serviceDetails.cabinNumber}` }}</span>
                </div>
                <div v-if="serviceDetails.deckNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-ship-wheel </v-icon>
                        <span class="text-body-2">{{ $t('cruises.deck_number') }}</span>
                    </span>
                    <span class="order-info-base-info__text">{{ `${serviceDetails.deckNumber}` }}</span>
                </div>
                <div v-if="serviceDetails.deckName" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small"> mdi-ship-wheel </v-icon>
                        <span class="text-body-2">{{ $t('cruises.deck_name') }}</span>
                    </span>
                    <span class="order-info-base-info__text"> {{ `${serviceDetails.deckName}` }} </span>
                </div>
                <template v-slot:extraTime>
                    <div v-if="serviceDetails.duration" class="order-info-base-info">
                        <span class="order-info-base-info__icon">
                            <v-icon start size="x-small">mdi-timer-outline</v-icon>
                            <span class="text-body-2">{{ $t('duration') }}</span>
                        </span>
                        <span class="order-info-base-info__text">
                            {{ `${serviceDetails.duration} ${$t('day', serviceDetails.duration)}` }}
                        </span>
                    </div>
                </template>
                <div v-if="service.status === 'Quote' && !isReadOnly" class="d-flex align-center">
                    <v-icon start size="small" color="primary" @click="fillCruiseBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span class="text-caption cursor-pointer text-primary" @click="fillCruiseBookingDataModal = true">
                        {{ $t('enter_details') }}
                    </span>
                    <fill-cruise-booking-data-modal
                        v-model="fillCruiseBookingDataModal"
                        :service="service"
                        :order="order"
                    />
                </div>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <cruise-info-modal
                ref="cruiseInfoModal"
                :product-name-for-supplier="service.productNameForSupplier"
                :is-info-loading="isInfoLoading"
                :service-name="service.serviceName"
            />
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Prop, Inject} from 'vue-facing-decorator'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import CruiseInfoModal from '~src/components/cruises/info/CruiseInfoModal.vue'
    import FillCruiseBookingDataModal from '@/components/account/modals/newOrderRequest/FillCruiseBookingDataModal.vue'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'

    @Component({
        components: {
            FillCruiseBookingDataModal,
            CruiseInfoModal,
            ServiceInfoDialog,
            OrderInfoBase,
            ServiceCard,
        },
    })
    export class CruiseService extends ServiceBase {
        @Prop({required: true}) service
        @Prop({default: false}) isPackage
        @Prop() isFirst
        @Inject({default: false}) isReadOnly

        fillCruiseBookingDataModal = false
        info = {}

        departureLocation = null
        arrivalLocation = null

        async showInfo() {
            if (!this.infoLoadingFinished) {
                await this.loadProductInfo()
            }

            this.$refs.cruiseInfoModal.showModal({info: this.info, service: this.service})
        }

        async loadProductInfo() {
            this.isInfoLoading = true

            const processId = this.service.processId.replace(/^.{6}/, '')
            try {
                this.info = await this.$api.cruiseInfo.get({processId})

                const departureCityId = this.serviceDetails?.departureLocation?.cityId
                const departureCountryIdId = this.serviceDetails?.departureLocation?.countryId
                const arrivalCityId = this.serviceDetails?.returnLocation?.cityId
                const arrivalCountryIdId = this.serviceDetails?.returnLocation?.countryId

                if (departureCityId) {
                    const {
                        cities: [dCity],
                    } = await this.$api.locations.get({
                        id: departureCityId,
                        countryId: departureCountryIdId,
                        limitCities: 1,
                    })

                    this.departureLocation = dCity
                }

                if (arrivalCityId) {
                    const {
                        cities: [aCity],
                    } = await this.$api.locations.get({
                        id: arrivalCityId,
                        countryId: arrivalCountryIdId,
                        limitCities: 1,
                    })

                    this.arrivalLocation = aCity
                }

                this.isInfoLoading = false
                this.infoLoadingFinished = true
            } catch (e) {
                /* empty */
            }
        }

        get mainImage() {
            if (this.info.images && this.info.images.length) {
                let image = this.info.images.find(el => el.mainImage)
                if (!image) {
                    image = this.info.images[0]
                }
                return image.url
            }
            return null
        }

        get serviceDetails() {
            return this.service.serviceDetails[0]
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default CruiseService
</script>

<style lang="scss" scoped>
    :deep(.v-dialog) {
        background: #fff;
        padding: 20px;
    }

    .modal-close-btn-block {
        position: relative;
        top: 12px;
        right: 12px;
    }
</style>
